import type { RouteLocationNormalized } from 'vue-router'

import type { AddToCartParams, DataLayerEvent, EventData } from '../../../types'
import { formatPriceAsNumber } from '../../../utils/formatPrice'

import { pushEvent } from './events.utils'

export function trackPageView(to: RouteLocationNormalized) {
  const event: DataLayerEvent = {
    event: 'page_view',
    page_info: {
      page_category: to.name,
      content_group: to.meta.tracking?.contentGroup ?? 'Unmapped',
    },
  }

  pushEvent(event)
}

export function trackViewSearchResults({ searchTerm }: { searchTerm: string }) {
  const event = { event: 'view_search_results', search_term: searchTerm }

  pushEvent(event)
}

export type SelectPromotionParams = {
  creativeName: string
  creativeSlot: string
}

export function trackSelectPromotion({
  creativeName,
  creativeSlot,
}: SelectPromotionParams): void {
  const event: DataLayerEvent = {
    event: 'select_promotion',
    ecommerce: {
      creative_name: creativeName,
      creative_slot: creativeSlot,
    },
  }

  pushEvent(event)
}

export function trackViewCart(payload: EventData): void {
  const event: DataLayerEvent = { event: 'view_cart', ...payload }

  pushEvent(event)
}

export function trackSelectItem(payload: EventData): void {
  const event: DataLayerEvent = { event: 'select_item', ...payload }

  pushEvent(event)
}

export function trackViewItemList(payload: EventData): void {
  const event: DataLayerEvent = { event: 'view_item_list', ...payload }

  pushEvent(event)
}

export function trackBeginCheckout(payload: EventData): void {
  const event: DataLayerEvent = { event: 'begin_checkout', ...payload }

  pushEvent(event)
}

export function trackAddPaymentInfo(payload: EventData): void {
  const event: DataLayerEvent = { event: 'add_payment_info', ...payload }

  pushEvent(event)
}

export function trackAddShippingInfo(payload: EventData): void {
  const event: DataLayerEvent = { event: 'add_shipping_info', ...payload }

  pushEvent(event)
}

export type ViewPromotionParams = {
  creativeName: string
  creativeSlot: string
}

export function trackViewPromotion({
  creativeName,
  creativeSlot,
}: ViewPromotionParams): void {
  const event: DataLayerEvent = {
    event: 'view_promotion',
    ecommerce: {
      creative_name: creativeName,
      creative_slot: creativeSlot,
    },
  }

  pushEvent(event)
}

export type ViewItemParams = {
  currency: string
  value: number
  item: {
    itemId: string
    itemUuid: string
    itemName: string
    affiliation: string
    discount: number
    itemBrand: string
    itemCategory: string
    itemVariant: string
    itemWarrantyDuration: string
    itemIsTradeInEligible: boolean
    locationId: string
    price: number
  }
}

export function trackViewItem({ currency, value, item }: ViewItemParams): void {
  const event: DataLayerEvent = {
    event: 'view_item',
    ecommerce: {
      currency,
      value: formatPriceAsNumber({ amount: value, currency }),
      items: [
        {
          item_legacy_id: item.itemId,
          item_id: item.itemUuid,
          item_name: item.itemName,
          affiliation: item.affiliation,
          discount: formatPriceAsNumber({ amount: item.discount, currency }),
          index: 0,
          item_brand: item.itemBrand,
          item_category: item.itemCategory,
          item_variant: item.itemVariant,
          item_warranty_duration: item.itemWarrantyDuration,
          item_is_trade_in_eligible: item.itemIsTradeInEligible,
          location_id: item.locationId,
          price: formatPriceAsNumber({ amount: item.price, currency }),
          quantity: 1,
          google_business_vertical: 'retail',
        },
      ],
    },
  }

  pushEvent(event)
}

export function trackPurchase(payload: EventData): void {
  const ecommerce = payload.ecommerce as Record<string, unknown> | undefined
  const currency = ecommerce?.currency
  const items = Array.isArray(ecommerce?.items)
    ? ecommerce.items.map((item) => {
        const source = item as Record<string, unknown>

        return {
          ...source,
          price: formatPriceAsNumber({ amount: source.price, currency }),
          discount: formatPriceAsNumber({ amount: source.discount, currency }),
          item_insurance_price: formatPriceAsNumber({
            amount: source.item_insurance_price,
            currency,
          }),
          item_mobile_plan_monthly_price: formatPriceAsNumber({
            amount: source.item_mobile_plan_monthly_price,
            currency,
          }),
          item_shipping_cost: formatPriceAsNumber({
            amount: source.item_shipping_cost,
            currency,
          }),
        }
      })
    : ecommerce?.items

  const event: DataLayerEvent = {
    ...payload,
    event: 'purchase_back_bone',
    ...(ecommerce
      ? {
          ecommerce: {
            ...ecommerce,
            value:
              formatPriceAsNumber({ amount: ecommerce.value, currency }) ?? 0,
            tax: formatPriceAsNumber({ amount: ecommerce.tax, currency }),
            shipping: formatPriceAsNumber({
              amount: ecommerce.shipping,
              currency,
            }),
            items,
          },
        }
      : {}),
  }

  pushEvent(event)
}

export type AddToFavoriteParams = {
  currency: string
  value: number
  item: ViewItemParams['item'] & {
    itemPriceWhenFavorited: number
    itemFavoritedLocation: string
  }
}

export function trackAddToFavorite({
  currency,
  value,
  item,
}: AddToFavoriteParams): void {
  const event: DataLayerEvent = {
    event: 'add_to_wishlist',
    ecommerce: {
      currency,
      value: formatPriceAsNumber({ amount: value, currency }),
      items: [
        {
          item_legacy_id: item.itemId,
          item_id: item.itemUuid,
          item_name: item.itemName,
          affiliation: item.affiliation,
          discount: formatPriceAsNumber({ amount: item.discount, currency }),
          index: 0,
          item_brand: item.itemBrand,
          item_category: item.itemCategory,
          item_variant: item.itemVariant,
          item_warranty_duration: item.itemWarrantyDuration,
          item_is_trade_in_eligible: item.itemIsTradeInEligible,
          location_id: item.locationId,
          price: formatPriceAsNumber({ amount: item.price, currency }),
          quantity: 1,
          google_business_vertical: 'retail',
          item_is_favorited: true,
          item_days_since_favorited: 0,
          item_price_when_favorited: formatPriceAsNumber({
            amount: item.itemPriceWhenFavorited,
            currency,
          }),
          item_favorited_location: item.itemFavoritedLocation,
        },
      ],
    },
  }

  pushEvent(event)
}

export function trackAddToCart({
  hasTradeIn,
  zone,
  currency,
  value,
  items,
}: AddToCartParams): void {
  const event: DataLayerEvent = {
    event: 'add_to_cart',
    add_to_cart_location: zone,
    has_trade_in: hasTradeIn,
    ecommerce: {
      currency,
      value: formatPriceAsNumber({ amount: value, currency }),
      items: items.map((item) => ({
        ...item,
        price: formatPriceAsNumber({ amount: item.price, currency }),
        discount: formatPriceAsNumber({ amount: item.discount, currency }),
        item_insurance_price: formatPriceAsNumber({
          amount: item.item_insurance_price,
          currency,
        }),
        item_mobile_plan_monthly_price: formatPriceAsNumber({
          amount: item.item_mobile_plan_monthly_price,
          currency,
        }),
        item_shipping_cost: formatPriceAsNumber({
          amount: item.item_shipping_cost,
          currency,
        }),
      })),
    },
  }

  pushEvent(event)
}

export function trackRemoveFromCart(payload: EventData): void {
  const event: DataLayerEvent = { event: 'remove_from_cart', ...payload }

  pushEvent(event)
}
